import { useMemo } from 'react'
import { i18n, useTranslation } from 'next-i18next'
import { ascend, omit, pick, prop, sort } from 'ramda'
import { gql, useLazyQuery, useQuery } from '@apollo/client'

import { isEmptyOrNull } from '@/utils'
import { getSearch, getUrlSlugs } from '@/utils/location'

const CITIES_IN_STATE_DATA_QUERY = gql`
  query ($input: String) {
    citiesInState(input: $input) {
      name
      country_code
      state
      location {
        lat
        lon
      }
      slug
    }
  }
`

const CITIES_DATA_QUERY = gql`
  query ($input: [String]) {
    cities(input: $input) {
      name
      country_code
      state
      location {
        lat
        lon
      }
      slug
    }
  }
`

export const CITY_DATA_QUERY = gql`
  query ($input: String) {
    city(input: $input) {
      name
      country_code
      state
      location {
        lat
        lon
      }
      slug
    }
  }
`

const CITY_AND_BUILDING_DATA_QUERY = gql`
  fragment buildingListFields on Listing {
    bathrooms
    bedrooms
    gr_min_price
    gr_max_price
    gr_unit
    gr_count
    hide_unit_number
    unit_type_txt_id
    unit_type_scope_txt_id
    cover_photo_aws_s3_key
    listing_id
    price
    price_frequency
    is_hidden
    state_machine
    verified_state_machine
    furnished
    size
    unit_files {
      aws_s3_key
      position
      tag
    }
    landlords {
      identity_verified_state
    }
  }
  fragment listingFields on FullListing {
    street_name
    building_type
    building_name
    building_id
    full_street_name
    city
    state
    zip
    location {
      lat
      lon
    }
    bathrooms
    bedrooms
    size
    gr_min_size
    gr_min_price
    gr_max_price
    gr_unit
    hide_unit_number
    unit_type_txt_id
    unit_type_scope_txt_id
    cover_photo_aws_s3_key
    listing_id
    price
    price_frequency
    is_hidden
    state_machine
    verified_state_machine
    furnished
    unit_files {
      aws_s3_key
      position
      tag
    }
    landlords {
      identity_verified_state
    }
  }
  query ($input: String, $buildingInput: ListSearchInput!) {
    city(input: $input) {
      name
      country_code
      state
      location {
        lat
        lon
      }
      slug
    }
    listSearch {
      buildings(input: $buildingInput) {
        metadata {
          page
          page_size
          total_count
          listings_count
        }
        feed {
          building_id
          street_name
          building_type
          full_street_name
          city
          state
          zip
          building_name
          location {
            lat
            lon
          }
          building_files {
            aws_s3_key
            tag
          }
          listings {
            ...buildingListFields
          }
          listing_count
        }
      }
      featured(input: $buildingInput) {
        ...listingFields
      }
    }
  }
`

const LOCALITY_DATA_QUERY = gql`
  query ($input: LocalityInput!) {
    locality(input: $input) {
      name
      city
      state
      country_code
      type
      slug {
        slug_name
        slug_city
      }
      location {
        lat
        lon
      }
      location_info {
        category
        subcategory
      }
      nearby {
        name
        city
        state
        slug {
          slug_name
          slug_city
        }
      }
    }
  }
`

const CITY_AND_LOCALITY_DATA_QUERY = gql`
  fragment buildingListFields on Listing {
    bathrooms
    bedrooms
    gr_min_price
    gr_max_price
    gr_unit
    gr_count
    hide_unit_number
    unit_type_txt_id
    unit_type_scope_txt_id
    cover_photo_aws_s3_key
    listing_id
    price
    price_frequency
    is_hidden
    state_machine
    verified_state_machine
    furnished
    size
    unit_files {
      aws_s3_key
      position
      tag
    }
    landlords {
      identity_verified_state
    }
  }
  fragment listingFields on FullListing {
    street_name
    building_type
    building_name
    building_id
    full_street_name
    city
    state
    zip
    location {
      lat
      lon
    }
    bathrooms
    bedrooms
    size
    gr_min_size
    gr_min_price
    gr_max_price
    gr_unit
    hide_unit_number
    unit_type_txt_id
    unit_type_scope_txt_id
    cover_photo_aws_s3_key
    listing_id
    price
    price_frequency
    is_hidden
    state_machine
    verified_state_machine
    furnished
    unit_files {
      aws_s3_key
      position
      tag
    }
    landlords {
      identity_verified_state
    }
  }
  query (
    $input: LocalityInput!
    $cityInput: String!
    $buildingInput: ListSearchInput!
  ) {
    locality(input: $input) {
      name
      city
      state
      country_code
      type
      slug {
        slug_name
        slug_city
      }
      location {
        lat
        lon
      }
      nearby {
        name
        city
        state
        slug {
          slug_name
          slug_city
        }
      }
    }
    city(input: $cityInput) {
      name
      country_code
      state
      location {
        lat
        lon
      }
      slug
    }
    listSearch {
      buildings(input: $buildingInput) {
        metadata {
          page
          page_size
          total_count
          listings_count
        }
        feed {
          building_id
          street_name
          building_type
          full_street_name
          city
          state
          zip
          building_name
          location {
            lat
            lon
          }
          building_files {
            aws_s3_key
            tag
          }
          listings {
            ...buildingListFields
          }
          listing_count
        }
      }
      featured(input: $buildingInput) {
        ...listingFields
      }
    }
  }
`

export const NEAREST_CITY_QUERY = gql`
  query ($input: NearestCitiesInput!) {
    nearestCities(input: $input) {
      name
      country_code
      state
      location {
        lat
        lon
      }
      slug
    }
  }
`

export const useCitiesInState = (stateAlphaCode = '') => {
  const alphaCode = stateAlphaCode.toLowerCase()
  const {
    data = [],
    error,
    loading
  } = useQuery(CITIES_IN_STATE_DATA_QUERY, {
    variables: {
      input: alphaCode
    }
  })
  const cities = data.citiesInState || []
  const sortedCities = sort(ascend(prop('name')), cities)
  return { data: sortedCities, error, loading }
}

export const useCitiesData = (citySlugs = []) => {
  const { t } = useTranslation()
  const {
    data = [],
    error,
    loading
  } = useQuery(CITIES_DATA_QUERY, {
    variables: {
      input: citySlugs
    }
  })
  const cities = data.cities || []
  const formattedCities = cities.map(cityData => {
    const translatedName = cityData.slug
      ? { translated: t(`cities:city_name_${cityData.slug}`, cityData.name) }
      : {}
    const translatedState = cityData.slug
      ? { translatedState: t(`states:${cityData.state}`, cityData.state) }
      : {}
    return { ...translatedName, ...translatedState, ...cityData }
  })
  return { data: formattedCities, error, loading }
}

export const useCityData = citySlug => {
  const { t } = useTranslation()
  const noCitySlug = isEmptyOrNull(citySlug)
  const {
    data = {},
    error,
    loading
  } = useQuery(CITY_DATA_QUERY, {
    skip: noCitySlug,
    pollInterval: 0,
    variables: {
      input: citySlug
    },
    fetchPolicy: 'cache-first'
  })

  const processedData = useMemo(() => {
    const cityData = data.city || {}

    if (!cityData.slug) {
      return cityData
    }

    return {
      ...cityData,
      translated: t(`cities:city_name_${cityData.slug}`, cityData.name),
      translatedState: t(`states:${cityData.state}`, cityData.state)
    }
  }, [data.city, t])

  return {
    data: processedData,
    error,
    loading
  }
}

export async function withCityAndBuildingData({
  buildingInput,
  apolloClient,
  query
}) {
  const { citySlug } = getUrlSlugs({ query })

  const { data } = await apolloClient.query({
    query: CITY_AND_BUILDING_DATA_QUERY,
    variables: {
      input: citySlug,
      buildingInput
    }
  })
  const city = data.city || {}
  const buildingDataServer = pick(['listSearch'], data) || {}

  const translatedName = city.slug
    ? {
        translated:
          i18n?.t(`cities:city_name_${city.slug}`, city.name) || city.name
      }
    : {}
  const translatedState = city.slug
    ? {
        translatedState:
          i18n?.t(`states:${city.state}`, city.state) || city.state
      }
    : {}
  const cityData = { ...translatedName, ...translatedState, ...city }
  return {
    cityData,
    buildingDataServer,
    neighborhoodData: {}
  }
}

export const useNeighbourhoodData = (neighbourhoodSlug, citySlug) => {
  const { t } = useTranslation()
  const {
    data = {},
    error,
    loading
  } = useQuery(LOCALITY_DATA_QUERY, {
    skip: !neighbourhoodSlug,
    variables: {
      input: {
        slug_name: neighbourhoodSlug,
        slug_city: citySlug
      }
    }
  })

  const locality = data.locality || {}
  const translatedName = locality.slug?.slug_name
    ? {
        translated: t(
          `seo:neighbourhood_name_${locality.slug.slug_name}_${locality.slug.slug_city}`,
          locality.name
        )
      }
    : {}
  const translatedState = locality.slug?.slug_name
    ? {
        translatedState: t(`seo:${locality.state}`, locality.state)
      }
    : {}
  return {
    data: { ...translatedName, ...translatedState, ...locality },
    error,
    loading
  }
}

// We can add cities here to make it easier and for cases where the replace doesn't work properly
const cityMapping = {
  'new-westminster': 'New Westminster',
  toronto: 'Toronto'
}

export function citySlugToName(slug) {
  return (
    cityMapping[slug] ??
    slug
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  )
}

export async function getCityAndNeighborhoodData({
  reduxStore,
  apolloClient,
  query,
  req
}) {
  const { dispatch } = reduxStore
  const search = getSearch({ query })
  const { citySlug, neighbourhoodSlug } = getUrlSlugs({ query })
  const filters = omit(
    ['state_machine'],
    reduxStore.getState().listings.filters
  )

  const buildingInput = filters

  dispatch.listings.updateFilter({
    sort: { sort_by: 'SUGGESTED', sort_type: 'ASC' },
    ...search
  })

  if (!neighbourhoodSlug && !citySlug) {
    return { cityData: {}, neighborhoodData: {} }
  }

  if (!neighbourhoodSlug) {
    return withCityAndBuildingData({ buildingInput, apolloClient, query })
  }

  const { data } = await apolloClient.query({
    query: CITY_AND_LOCALITY_DATA_QUERY,
    variables: {
      input: {
        slug_name: neighbourhoodSlug,
        slug_city: citySlug
      },
      cityInput: citySlug,
      buildingInput
    }
  })
  await dispatch.listings.updateSearchPreferences(req)

  const locality = data.locality || {}
  const cityData = data.city || {}
  const buildingDataServer = pick(['listSearch'], data) || {}

  const translatedName = locality.slug?.slug_name
    ? {
        translated:
          i18n?.t(
            `seo:neighbourhood_name_${locality.slug.slug_name}_${locality.slug.slug_city}`,
            locality.name
          ) || locality.name
      }
    : {}
  const translatedState = locality.slug?.slug_name
    ? {
        translatedState:
          i18n?.t(`states:${locality.state}`, locality.state) || locality.state
      }
    : {}

  const neighborhoodData = {
    ...translatedName,
    ...translatedState,
    ...locality
  }
  return {
    cityData,
    neighborhoodData,
    buildingDataServer
  }
}

export const useNearestCities = () => {
  return useLazyQuery(NEAREST_CITY_QUERY, {
    pollInterval: 0,
    fetchPolicy: 'cache-first'
  })
}

export const useStateData = (stateAlphaCode = '') => {
  const { t } = useTranslation()
  const alphaCode = stateAlphaCode.toLowerCase()
  const STATES = {
    ab: {
      name: t('states:Alberta'),
      shortName: t('states:AB'),
      slug: 'alberta',
      country: 'CA'
    },
    bc: {
      name: t('states:British Columbia'),
      shortName: t('states:BC'),
      slug: 'british-columbia',
      country: 'CA'
    },
    mb: {
      name: t('states:Manitoba'),
      shortName: t('states:MB'),
      slug: 'manitoba',
      country: 'CA'
    },
    nb: {
      name: t('states:New Brunswick'),
      shortName: t('states:NB'),
      slug: 'new-brunswick',
      country: 'CA'
    },
    nl: {
      name: t('states:Newfoundland and Labrador'),
      shortName: t('states:NL'),
      slug: 'newfoundland-and-labrador',
      country: 'CA'
    },
    nt: {
      name: t('states:Northwest Territories'),
      shortName: t('states:NT'),
      slug: 'northwest-territories',
      country: 'CA'
    },
    ns: {
      name: t('states:Nova Scotia'),
      shortName: t('states:NS'),
      slug: 'nova-scotia',
      country: 'CA'
    },
    on: {
      name: t('states:Ontario'),
      shortName: t('states:ON'),
      slug: 'ontario',
      country: 'CA'
    },
    pe: {
      name: t('states:Prince Edward Island'),
      shortName: t('states:PE'),
      slug: 'prince-edward-island',
      country: 'CA'
    },
    qc: {
      name: t('states:Quebec'),
      shortName: t('states:QC'),
      slug: 'quebec',
      country: 'CA'
    },
    sk: {
      name: t('states:Saskatchewan'),
      shortName: t('states:SK'),
      slug: 'saskatchewan',
      country: 'CA'
    },
    al: {
      name: t('states:Alabama'),
      shortName: t('states:AL'),
      slug: 'alabama',
      country: 'US'
    },
    ak: {
      name: t('states:Alaska'),
      shortName: t('states:AK'),
      slug: 'alaska',
      country: 'US'
    },
    az: {
      name: t('states:Arizona'),
      shortName: t('states:AZ'),
      slug: 'arizona',
      country: 'US'
    },
    ar: {
      name: t('states:Arkansas'),
      shortName: t('states:AR'),
      slug: 'arkansas',
      country: 'US'
    },
    ca: {
      name: t('states:California'),
      shortName: t('states:CA'),
      slug: 'california',
      country: 'US'
    },
    co: {
      name: t('states:Colorado'),
      shortName: t('states:CO'),
      slug: 'colorado',
      country: 'US'
    },
    ct: {
      name: t('states:Connecticut'),
      shortName: t('states:CT'),
      slug: 'connecticut',
      country: 'US'
    },
    de: {
      name: t('states:Delaware'),
      shortName: t('states:DE'),
      slug: 'delaware',
      country: 'US'
    },
    fl: {
      name: t('states:Florida'),
      shortName: t('states:FL'),
      slug: 'florida',
      country: 'US'
    },
    ga: {
      name: t('states:Georgia'),
      shortName: t('states:GA'),
      slug: 'georgia',
      country: 'US'
    },
    hi: {
      name: t('states:Hawaii'),
      shortName: t('states:HI'),
      slug: 'hawaii',
      country: 'US'
    },
    id: {
      name: t('states:Idaho'),
      shortName: t('states:ID'),
      slug: 'idaho',
      country: 'US'
    },
    il: {
      name: t('states:Illinois'),
      shortName: t('states:IL'),
      slug: 'illinois',
      country: 'US'
    },
    in: {
      name: t('states:Indiana'),
      shortName: t('states:IN'),
      slug: 'indiana',
      country: 'US'
    },
    ia: {
      name: t('states:Iowa'),
      shortName: t('states:IA'),
      slug: 'iowa',
      country: 'US'
    },
    ks: {
      name: t('states:Kansas'),
      shortName: t('states:KS'),
      slug: 'kansas',
      country: 'US'
    },
    ky: {
      name: t('states:Kentucky'),
      shortName: t('states:KY'),
      slug: 'kentucky',
      country: 'US'
    },
    la: {
      name: t('states:Louisiana'),
      shortName: t('states:LA'),
      slug: 'louisiana',
      country: 'US'
    },
    me: {
      name: t('states:Maine'),
      shortName: t('states:ME'),
      slug: 'maine',
      country: 'US'
    },
    md: {
      name: t('states:Maryland'),
      shortName: t('states:MD'),
      slug: 'maryland',
      country: 'US'
    },
    ma: {
      name: t('states:Massachusetts'),
      shortName: t('states:MA'),
      slug: 'massachusetts',
      country: 'US'
    },
    mi: {
      name: t('states:Michigan'),
      shortName: t('states:MI'),
      slug: 'michigan',
      country: 'US'
    },
    mn: {
      name: t('states:Minnesota'),
      shortName: t('states:MN'),
      slug: 'minnesota',
      country: 'US'
    },
    ms: {
      name: t('states:Mississippi'),
      shortName: t('states:MS'),
      slug: 'mississippi',
      country: 'US'
    },
    mo: {
      name: t('states:Missouri'),
      shortName: t('states:MO'),
      slug: 'missouri',
      country: 'US'
    },
    mt: {
      name: t('states:Montana'),
      shortName: t('states:MT'),
      slug: 'montana',
      country: 'US'
    },
    ne: {
      name: t('states:Nebraska'),
      shortName: t('states:NE'),
      slug: 'nebraska',
      country: 'US'
    },
    nv: {
      name: t('states:Nevada'),
      shortName: t('states:NV'),
      slug: 'nevada',
      country: 'US'
    },
    nh: {
      name: t('states:New Hampshire'),
      shortName: t('states:NH'),
      slug: 'new-hampshire',
      country: 'US'
    },
    nj: {
      name: t('states:New Jersey'),
      shortName: t('states:NJ'),
      slug: 'new-jersey',
      country: 'US'
    },
    nm: {
      name: t('states:New Mexico'),
      shortName: t('states:NM'),
      slug: 'new-mexico',
      country: 'US'
    },
    ny: {
      name: t('states:New York'),
      shortName: t('states:NY'),
      slug: 'new-york',
      country: 'US'
    },
    nc: {
      name: t('states:North Carolina'),
      shortName: t('states:NC'),
      slug: 'north-carolina',
      country: 'US'
    },
    nd: {
      name: t('states:North Dakota'),
      shortName: t('states:ND'),
      slug: 'north-dakota',
      country: 'US'
    },
    oh: {
      name: t('states:Ohio'),
      shortName: t('states:OH'),
      slug: 'ohio',
      country: 'US'
    },
    ok: {
      name: t('states:Oklahoma'),
      shortName: t('states:OK'),
      slug: 'oklahoma',
      country: 'US'
    },
    or: {
      name: t('states:Oregon'),
      shortName: t('states:OR'),
      slug: 'oregon',
      country: 'US'
    },
    pa: {
      name: t('states:Pennsylvania'),
      shortName: t('states:PA'),
      slug: 'pennsylvania',
      country: 'US'
    },
    pr: {
      name: t('states:Puerto Rico'),
      shortName: t('states:PR'),
      slug: 'puerto-rico',
      country: 'US'
    },
    ri: {
      name: t('states:Rhode Island'),
      shortName: t('states:RI'),
      slug: 'rhode-island',
      country: 'US'
    },
    sc: {
      name: t('states:South Carolina'),
      shortName: t('states:SC'),
      slug: 'south-carolina',
      country: 'US'
    },
    sd: {
      name: t('states:South Dakota'),
      shortName: t('states:SD'),
      slug: 'south-dakota',
      country: 'US'
    },
    tn: {
      name: t('states:Tennessee'),
      shortName: t('states:TN'),
      slug: 'tennessee',
      country: 'US'
    },
    tx: {
      name: t('states:Texas'),
      shortName: t('states:TX'),
      slug: 'texas',
      country: 'US'
    },
    ut: {
      name: t('states:Utah'),
      shortName: t('states:UT'),
      slug: 'utah',
      country: 'US'
    },
    vt: {
      name: t('states:Vermont'),
      shortName: t('states:VT'),
      slug: 'vermont',
      country: 'US'
    },
    va: {
      name: t('states:Virginia'),
      shortName: t('states:VA'),
      slug: 'virginia',
      country: 'US'
    },
    wa: {
      name: t('states:Washington'),
      shortName: t('states:WA'),
      slug: 'washington',
      country: 'US'
    },
    dc: {
      name: t('states:Washington DC'),
      shortName: t('states:DC'),
      slug: 'washington-dc',
      country: 'US'
    },
    wv: {
      name: t('states:West Virginia'),
      shortName: t('states:WV'),
      slug: 'west-virginia',
      country: 'US'
    },
    wi: {
      name: t('states:Wisconsin'),
      shortName: t('states:WI'),
      slug: 'wisconsin',
      country: 'US'
    },
    wy: {
      name: t('states:Wyoming'),
      shortName: t('states:WY'),
      slug: 'wyoming',
      country: 'US'
    }
  }

  return stateAlphaCode ? STATES[alphaCode] || {} : STATES
}
